import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { gql } from "~/__generated__"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { TagField, TagFieldTag } from "~/fields/tag-field"
import { Button } from "~/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/ui/dialog"
import { GraphqlError } from "~/ui/errors"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/ui/form"
import { Input } from "~/ui/input"
import { useToast } from "~/ui/use-toast"

const createInvitationMutation = gql(/* GraphQL */ `
  mutation CreateInvitation($input: InvitationCreateInput!) {
    invitationCreate(input: $input) {
      invitation {
        id
        title
        token
        status
        contactsAddedCount
        createdAt
        inviteUrl
        tags {
          id
          name
        }
      }
    }
  }
`)

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  tagInput: z.string().optional(),
})

type FormData = z.infer<typeof formSchema>

interface InvitationCreateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  redirectOnSuccess?: () => void
}

export function InvitationCreateDialog({
  open,
  onOpenChange,
  onSuccess,
  redirectOnSuccess,
}: InvitationCreateDialogProps) {
  const { toast } = useToast()
  const [selectedTags, setSelectedTags] = useState<TagFieldTag[]>([])

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      tagInput: "",
    },
  })

  const [createInvitation, { loading: creating, error }] = useSafeMutation(createInvitationMutation)

  const handleSubmit = async (data: FormData) => {
    try {
      const existingTags = selectedTags.filter((tag) => !tag.isNew)
      const newTagsFromSelected = selectedTags.filter((tag) => tag.isNew)
      const tagIds = existingTags.length > 0 ? existingTags.map((tag) => tag.id) : undefined
      const newTags = [
        ...newTagsFromSelected.map((tag) => tag.name),
        ...(data.tagInput?.trim() ? [data.tagInput.trim()] : []),
      ]

      const result = await createInvitation({
        variables: {
          input: {
            invitationInput: {
              title: data.title,
              tagIds: tagIds,
              newTags: newTags.length > 0 ? newTags : undefined,
            },
          },
        },
        refetchQueries: ["GetInvitations"],
      })

      if (result.data?.invitationCreate?.invitation) {
        const invitation = result.data.invitationCreate.invitation

        toast({
          title: "Invitation created",
          description: `"${invitation.title}" has been created successfully.`,
        })

        // Copy link to clipboard
        await navigator.clipboard.writeText(invitation.inviteUrl)

        toast({
          title: "Link copied",
          description: "Invitation link has been copied to your clipboard.",
        })

        form.reset()
        setSelectedTags([])
        onOpenChange(false)
        onSuccess?.()
        redirectOnSuccess?.()
      }
    } catch (err) {
      console.error("Error creating invitation:", err)
      toast({
        title: "Error",
        description: "Failed to create invitation. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleClose = () => {
    form.reset()
    setSelectedTags([])
    onOpenChange(false)
  }

  if (error) {
    return <GraphqlError error={error} />
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!max-h-[90vh] !max-w-[90vw] !overflow-y-auto !p-3 sm:!max-w-md sm:!p-6"
        onInteractOutside={handleClose}
      >
        <DialogHeader>
          <DialogTitle>Invite Friends to Join Your Contacts</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 sm:space-y-6">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Invite Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Title goes here..." autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2 sm:space-y-3">
              <p className="text-sm text-muted-foreground">
                Tags are optional and will be applied to anyone who joins your address book using
                this link.
              </p>
              <TagField
                control={form.control}
                name="tagInput"
                label="Add Tag"
                selectedTags={selectedTags}
                onTagsChange={setSelectedTags}
                disabled={creating}
              />
            </div>

            <div className="flex gap-3 pt-3 sm:pt-4">
              <Button type="submit" className="flex-1" disabled={creating}>
                {creating ? "Creating..." : "Save Invite Link"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
